import React, { useEffect, useRef } from "react";

// Dialog used for every create/edit form and every destructive confirmation.
// Closes on Escape and on backdrop click, moves focus in on open, and restores
// it on close so keyboard users don't lose their place in the table.
export default function Modal({ title, onClose, children, footer }) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    panelRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Stop the table behind the dialog from scrolling under it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 py-10"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-2xl border border-sand bg-white shadow-xl"
      >
        <div className="border-b border-sand px-6 py-4">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight">
            {title}
          </h2>
        </div>
        <div className="px-6 py-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-sand bg-paper px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
